import { Flexbox } from 'd2/components/Layout'
import {
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react'
import BlankButton from 'd2/components/ButtonV2/BlankButton'
import Popover from '@mui/material/Popover'
import Slider from '@mui/material/Slider'
import invariant from 'invariant'
import useStyles from './styles'
import type { Props } from './types'

const VolumeButton = memo<Props>(({
  className,
  setVolume,
  volume,
}) => {
  const { classes, cx } = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)

  const closeVolume = useCallback(() => setAnchorEl(null), [setAnchorEl])
  const openVolume = useCallback((event: Event | React.SyntheticEvent<any>) => setAnchorEl(event.currentTarget), [setAnchorEl])

  useEffect(() => {
    if (typeof window === 'undefined') return

    window.addEventListener('scroll', closeVolume)
    window.addEventListener('resize', closeVolume)
    return () => {
      window.removeEventListener('scroll', closeVolume)
      window.removeEventListener('resize', closeVolume)
    }
  }, [closeVolume])

  const onChange = useCallback((_event: Event, value: number[] | number) => {
    invariant(typeof value === 'number', 'value must be number. Got %s', value)
    setVolume(value)
  }, [setVolume])

  return (
    <>
      <BlankButton
        className={cx(classes.iconButton, className)}
        icon={volume ? 'volume-up' : 'volume-off'}
        noPadding
        onClick={openVolume}
      />
      { Boolean(anchorEl) && (
        <Popover
          anchorEl={anchorEl}
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'bottom',
          }}
          classes={{ paper: classes.popoverPaper }}
          keepMounted
          onClose={closeVolume}
          open={Boolean(anchorEl)}
          transformOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
        >
          <Flexbox
            align='center'
            className={classes.sliderContainer}
          >
            <Slider
              classes={{
                rail: classes.sliderRail,
                root: classes.sliderRoot,
                thumb: classes.sliderThumb,
                track: classes.sliderTrack,
              }}
              max={1}
              min={0}
              onChange={onChange}
              orientation='vertical'
              step={0.01}
              value={volume}
            />
          </Flexbox>
        </Popover>
      ) }
    </>
  )
})

VolumeButton.displayName = 'VolumeButton'

export default VolumeButton
